import { IconButton, TextField, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";

import { DrawerFormAddButton, DrawerFormRow, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField } from "../../../../components/forms";
import { hasCustomerUsersInput } from "../../../../lib/customer/users";
import { useTranslation } from "react-i18next";

type CustomerUsersFieldProps = {
  users: string[];
  onChange: (users: string[]) => void;
};

const CustomerUsersField = ({ users, onChange }: CustomerUsersFieldProps) => {
  const { t } = useTranslation();
  const normalizedUsers = users.length > 0 ? users : [""];
  const hasValidUsers = hasCustomerUsersInput(normalizedUsers);

  const addUser = () => {
    onChange([...normalizedUsers, ""]);
  };

  const removeUser = (index: number) => {
    if (normalizedUsers.length <= 1) {
      return;
    }
    onChange(normalizedUsers.filter((_, i) => i !== index));
  };

  const updateUser = (index: number, value: string) => {
    onChange(normalizedUsers.map((user, i) => (i === index ? value : user)));
  };

  return (
    <DrawerFormSection
      title={`${t("common.users")} *`}
      description={t("common.atLeastOneUser")}
      action={<DrawerFormAddButton onClick={addUser} label={t("common.addUser")} />}
    >
      {normalizedUsers.map((user, index) => (
        <DrawerFormRow key={index}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("common.username")}
            value={user}
            onChange={(event) => updateUser(index, event.target.value)}
            autoComplete="off"
            required={index === 0}
          />
          <IconButton
            size="small"
            onClick={() => removeUser(index)}
            color="error"
            aria-label={t("common.remove")}
            disabled={normalizedUsers.length <= 1}
          >
            <Icon>remove</Icon>
          </IconButton>
        </DrawerFormRow>
      ))}

      <FormHiddenField
        name="users"
        value={JSON.stringify(normalizedUsers.map((user) => user.trim()).filter(Boolean))}
      />
      <input
        name="usersRequired"
        value={hasValidUsers ? "1" : ""}
        required
        tabIndex={-1}
        aria-hidden
        style={{
          position: "absolute",
          opacity: 0,
          height: 0,
          width: 0,
          pointerEvents: "none",
        }}
        readOnly
      />

      {!hasValidUsers ? (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          Enter at least one username.
        </Typography>
      ) : null}
    </DrawerFormSection>
  );
};

export default CustomerUsersField;
